import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { Overline } from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { COMPANY, SERVICE_OPTIONS, PROPERTY_OPTIONS, IMG } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_METHODS = ["Email", "Phone", "WhatsApp"];

const EMPTY = {
  full_name: "",
  company_name: "",
  email: "",
  phone: "",
  service_required: "",
  property_type: "",
  message: "",
  preferred_contact: "Email",
};

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your full name";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Please enter a valid email";
    if (!form.phone.trim()) e.phone = "Please enter a phone number";
    if (!form.service_required) e.service_required = "Please select a service";
    if (!form.property_type) e.property_type = "Please select a property type";
    if (!form.message.trim()) e.message = "Please tell us about your requirements";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      toast.success("Enquiry submitted successfully.");
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <PageHeader
        eyebrow="Contact"
        title="Let's Talk About Your Property Requirements"
        subtitle="Get in touch to discuss your cleaning, horticultural, hygiene, supply or facility support requirements."
        image={IMG.lobby}
      />

      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid gap-14 lg:grid-cols-12">
          {/* Details */}
          <div className="lg:col-span-4">
            <Overline>Reach Us</Overline>
            <h2 className="mt-6 font-display text-2xl md:text-3xl text-sml-charcoal">Contact details</h2>
            <ul className="mt-8 space-y-6">
              <li>
                <a href={COMPANY.phoneHref} data-testid="contact-phone" className="group flex items-start gap-4">
                  <span className="grid place-items-center h-11 w-11 rounded-sm bg-sml-mist text-sml-forest group-hover:bg-sml-forest group-hover:text-white transition-colors"><Phone className="h-5 w-5" /></span>
                  <span><span className="block text-xs text-sml-charcoal/50 uppercase tracking-wide">Phone</span><span className="font-medium text-sml-charcoal">{COMPANY.phone}</span></span>
                </a>
              </li>
              <li>
                <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="group flex items-start gap-4">
                  <span className="grid place-items-center h-11 w-11 rounded-sm bg-sml-mist text-sml-forest group-hover:bg-sml-forest group-hover:text-white transition-colors"><Mail className="h-5 w-5" /></span>
                  <span><span className="block text-xs text-sml-charcoal/50 uppercase tracking-wide">Email</span><span className="font-medium text-sml-charcoal break-all">{COMPANY.email}</span></span>
                </a>
              </li>
              <li className="flex items-start gap-4">
                <span className="grid place-items-center h-11 w-11 rounded-sm bg-sml-mist text-sml-forest"><MapPin className="h-5 w-5" /></span>
                <span><span className="block text-xs text-sml-charcoal/50 uppercase tracking-wide">Address</span><span className="font-medium text-sml-charcoal">{COMPANY.address}</span></span>
              </li>
            </ul>
            <a href={COMPANY.productUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-product-link" className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-sml-forest hover:gap-3 transition-all">
              Browse Our Product Range <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>

          {/* Form */}
          <div className="lg:col-span-8">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="contact-success"
                className="rounded-sm border border-sml-green/30 bg-sml-mist p-12 text-center"
              >
                <CheckCircle2 className="h-14 w-14 text-sml-green mx-auto" />
                <h3 className="mt-6 font-display text-2xl md:text-3xl text-sml-charcoal">Thank you for contacting SML Property Solutions.</h3>
                <p className="mt-4 text-sml-charcoal/70 max-w-lg mx-auto">
                  Our team will review your enquiry and get back to you shortly.
                </p>
                <button
                  onClick={() => { setForm(EMPTY); setDone(false); }}
                  data-testid="contact-reset"
                  className="mt-8 inline-flex items-center gap-2 rounded-sm bg-sml-forest text-white font-medium px-6 py-3 hover:bg-sml-green transition-colors"
                >
                  Submit another enquiry
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="rounded-sm border border-sml-border p-7 lg:p-10">
                <div className="grid gap-6 sm:grid-cols-2">
                  <Field label="Full Name *" error={errors.full_name}>
                    <Input data-testid="input-full-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Your name" />
                  </Field>
                  <Field label="Company Name" error={errors.company_name}>
                    <Input data-testid="input-company" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Company / organisation" />
                  </Field>
                  <Field label="Email Address *" error={errors.email}>
                    <Input data-testid="input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.co.za" />
                  </Field>
                  <Field label="Phone Number *" error={errors.phone}>
                    <Input data-testid="input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="0XX XXX XXXX" />
                  </Field>
                  <Field label="Service Required *" error={errors.service_required}>
                    <Select value={form.service_required} onValueChange={(v) => set("service_required", v)}>
                      <SelectTrigger data-testid="select-service"><SelectValue placeholder="Select a service" /></SelectTrigger>
                      <SelectContent>
                        {SERVICE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Property Type *" error={errors.property_type}>
                    <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                      <SelectTrigger data-testid="select-property"><SelectValue placeholder="Select property type" /></SelectTrigger>
                      <SelectContent>
                        {PROPERTY_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="mt-6">
                  <Field label="Message / Requirements *" error={errors.message}>
                    <Textarea data-testid="input-message" rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us about your property and what you need..." />
                  </Field>
                </div>

                <div className="mt-6">
                  <Label className="text-sm font-medium text-sml-charcoal">Preferred Contact Method</Label>
                  <div className="mt-3 flex flex-wrap gap-2.5">
                    {CONTACT_METHODS.map((m) => (
                      <button
                        type="button"
                        key={m}
                        data-testid={`contact-method-${m.toLowerCase()}`}
                        onClick={() => set("preferred_contact", m)}
                        className={`rounded-sm border px-4 py-2 text-sm font-medium transition-colors ${form.preferred_contact === m ? "bg-sml-forest text-white border-sml-forest" : "border-sml-border text-sml-charcoal/70 hover:border-sml-forest"}`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="group mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-sm bg-sml-bronze text-sml-forest font-semibold px-8 py-4 hover:bg-sml-forest hover:text-white transition-colors active:scale-[0.98] disabled:opacity-60"
                >
                  {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Sending...</> : <>Request a Service Proposal <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Map */}
      <section data-testid="contact-map" className="bg-sml-mist">
        <div className="h-[440px] w-full">
          <iframe
            title="SML Property Solutions location"
            src="https://www.google.com/maps?q=Centurion,%20Gauteng,%20South%20Africa&output=embed"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </main>
  );
}

const Field = ({ label, error, children }) => (
  <div>
    <Label className="text-sm font-medium text-sml-charcoal">{label}</Label>
    <div className="mt-2">{children}</div>
    {error && <p className="mt-1.5 text-xs text-destructive" data-testid="field-error">{error}</p>}
  </div>
);
